// src/components/LandingPage.jsx
import { Link, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  FaHome,
  FaClipboardCheck,
  FaBell,
  FaUsers,
  FaMobile,
  FaShieldAlt,
  FaArrowRight,
  FaCheckCircle,
  FaStar
} from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';

const LandingPage = () => {
  const { isAuthenticated } = useSelector((state) => state.auth);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const features = [
    {
      icon: <FaClipboardCheck className="text-4xl text-blue-600" />,
      title: "Task Management",
      description: "Track all contract-to-close tasks with intuitive workflow management"
    },
    {
      icon: <FaBell className="text-4xl text-green-600" />,
      title: "Smart Notifications",
      description: "Automated reminders for upcoming deadlines and past-due tasks"
    },
    {
      icon: <FaUsers className="text-4xl text-purple-600" />,
      title: "Multi-Role Support",
      description: "Agents, coordinators, buyers, and sellers all in one platform"
    },
    {
      icon: <FaMobile className="text-4xl text-orange-600" />,
      title: "Mobile Responsive",
      description: "Access your contracts anywhere on desktop, tablet, or mobile"
    },
    {
      icon: <FaShieldAlt className="text-4xl text-red-600" />,
      title: "Secure & Reliable",
      description: "Enterprise-grade security with AWS cloud infrastructure"
    },
    {
      icon: <FaHome className="text-4xl text-indigo-600" />,
      title: "Real Estate Focused",
      description: "Built specifically for real estate professionals by industry experts"
    }
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "Real Estate Agent",
      content: "Contract To Keys has transformed how I manage my transactions. No more missed deadlines!",
      rating: 5
    },
    {
      name: "Mike Chen",
      role: "Transaction Coordinator",
      content: "The workflow management is incredible. I can handle twice as many contracts now.",
      rating: 5
    },
    {
      name: "Lisa Rodriguez",
      role: "Broker",
      content: "My entire team uses C2K. It's streamlined our entire closing process.",
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">

      <Helmet>
        <title>Real Estate Transaction Management Software — Contract To Keys</title>
        <meta name="description" content="Stop missing closing deadlines. Contract To Keys gives real estate agents and transaction coordinators a structured 13-step workflow from contract acceptance to keys. Free 60-day trial." />
        <meta name="keywords" content="real estate transaction management, closing checklist software, contract to close, realtor tools, transaction coordinator software" />
        <link rel="canonical" href="https://contracttokeys.com/" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              Contract To Keys
            </h1>
            <p className="text-xl md:text-2xl mb-4 text-blue-100">
              Your Closing Countdown
            </p>
            <p className="text-lg md:text-xl mb-8 max-w-3xl mx-auto text-blue-50">
              Streamline your real estate transactions from contract acceptance to successful closing.
              Never miss a deadline again with our intelligent task management platform.
            </p>
            <div className="flex flex-col items-center">
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/signup"
                  className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-50 transition-colors duration-300 flex items-center justify-center"
                >
                  Get Started (60 day free trial)
                  <FaArrowRight className="ml-2" />
                </Link>
                <Link
                  to="/about"
                  className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-blue-600 transition-colors duration-300"
                >
                  Learn More
                </Link>
                <Link
                  to="/contact"
                  className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-blue-600 transition-colors duration-300"
                >
                  Contact
                </Link>
              </div>
              <div className="text-sm italic text-blue-200 mt-2">No Credit Card Required</div>
            </div>



          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Everything You Need to Close Successfully
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Designed specifically for real estate professionals to eliminate delays,
              reduce mistakes, and create seamless transaction experiences.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-gray-50 p-8 rounded-xl hover:shadow-lg transition-shadow duration-300">
                <div className="mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="py-20 bg-gradient-to-r from-gray-900 to-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">
                The Real Estate Challenge
              </h2>
              <div className="space-y-4 text-lg">
                <p>Between contract acceptance and closing, countless obstacles can derail your deals:</p>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <FaCheckCircle className="text-red-400 mr-3" />
                    Financing complications and appraisal issues
                  </li>
                  <li className="flex items-center">
                    <FaCheckCircle className="text-red-400 mr-3" />
                    Inspection problems and title complications
                  </li>
                  <li className="flex items-center">
                    <FaCheckCircle className="text-red-400 mr-3" />
                    Missed deadlines and communication breakdowns
                  </li>
                  <li className="flex items-center">
                    <FaCheckCircle className="text-red-400 mr-3" />
                    Buyer's remorse and seller complications
                  </li>
                </ul>
              </div>
            </div>
            <div>
              <h2 className="text-4xl font-bold mb-6">
                Our Solution
              </h2>
              <div className="space-y-4 text-lg">
                <p>Contract To Keys eliminates these pain points with:</p>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <FaCheckCircle className="text-green-400 mr-3" />
                    Automated task tracking and deadline management
                  </li>
                  <li className="flex items-center">
                    <FaCheckCircle className="text-green-400 mr-3" />
                    Real-time notifications for all stakeholders
                  </li>
                  <li className="flex items-center">
                    <FaCheckCircle className="text-green-400 mr-3" />
                    Centralized communication and document flow
                  </li>
                  <li className="flex items-center">
                    <FaCheckCircle className="text-green-400 mr-3" />
                    Complete visibility from contract to keys
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Transform Your Real Estate Business?
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            Join real estate professionals who trust Contract To Keys
            to manage their transactions from contract to closing.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/signup"
              className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-50 transition-colors duration-300 flex items-center justify-center"
            >
              Start Your Free 60 Day Trial
              <FaArrowRight className="ml-2" />
            </Link>
            <Link
              to="/login"
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-blue-600 transition-colors duration-300"
            >
              Sign In
            </Link>
          </div>
          <p className="text-sm text-blue-200 mt-4">
            No credit card required • Free 60-day trial • Cancel anytime
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <h3 className="text-2xl font-bold mb-4">Contract To Keys</h3>
              <p className="text-gray-400 mb-4">
                Streamlining real estate transactions from contract acceptance to successful closing.
              </p>
              <p className="text-gray-400">
                Built by real estate professionals, for real estate professionals.
              </p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-gray-400 hover:text-white">About</Link></li>
                <li><Link to="/signup" className="text-gray-400 hover:text-white">Sign Up</Link></li>
                <li><Link to="/login" className="text-gray-400 hover:text-white">Login</Link></li>
                <li><Link to="/invitation" className="text-gray-400 hover:text-white">Share</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white">Contact Us</Link></li>

              </ul>
            </div>
          </div>

        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
